<script setup>
import { computed } from "vue";
import { breakpointsBootstrapV5, useBreakpoints } from "@vueuse/core";
import Popper from "vue-popperjs";

import { useSocialSharing } from "@tinyurldev/tu-ui-vue";

import Network from "@Components/sharing/Network.vue";

const props = defineProps({
  networks: {
    type: Array,
    default: () => ["facebook", "twitter", "whatsapp", "linkedin", "email"],
  },

  prefix: {
    type: String,
    required: true,
  },

  size: {
    type: String,
    default: "",
  },

  url: {
    type: String,
    default: "",
  },
});

const { smallerOrEqual } = useBreakpoints(breakpointsBootstrapV5);
const { shareableNetworks, buildShareableUrl } = useSocialSharing(false);

const isMobileView = smallerOrEqual("xl");
const availableNetworks = computed(() => {
  const socialShareableIcons = {
    facebook: "fa-fw fa-xl fa-brands fa-facebook-f",
    twitter: "fa-fw fa-xl fa-brands fa-x-twitter",
    whatsapp: "fa-fw fa-xl fa-brands fa-whatsapp",
    linkedin: "fa-fw fa-xl fa-brands fa-linkedin",
    email: "fa-fw fa-xl fa fa-envelope",
  };

  return props.networks.map((network) => {
    const item = shareableNetworks[network];

    return {
      ...item,
      icon: socialShareableIcons[item.id],
    };
  });
});

/**
 * Open a new window to share the tinyurl.
 */
function handleSharing(shortenedUrl, selectedNetwork) {
  const shareableUrl = buildShareableUrl(shortenedUrl, selectedNetwork);

  window.open(shareableUrl, "_blank");
}
</script>

<template>
  <Popper
    class="mr-1 mb-1"
    trigger="clickToToggle"
    :options="{
      placement: 'bottom',
      modifiers: { offset: { offset: '0,10' } },
    }"
  >
    <div :class="['popper', `_share-menu-${props.prefix}`]">
      <div class="px-3 mt-1 mb-2">
        <i class="fa fa-bullhorn fa-xl fa-fw" />
        Share it
      </div>
      <Network
        v-for="network in availableNetworks"
        :id="network.buttonId"
        :key="network.id"
        :class="['btn w-100 btn-sm py-0 px-3 mb-1', `_share-${network.id}`]"
        :title="`Share TinyURL on ${network.name}`"
        :aria-description="`Share TinyURL on ${network.name}`"
        :icon="network.icon"
        :name="network.name"
        @share="handleSharing(props.url, network)"
      />
    </div>

    <button
      slot="reference"
      v-tooltip="{
        content: 'Share your URL on social media',
        placement: 'top',
        trigger: isMobileView ? 'click' : 'focus hover',
      }"
      type="button"
      :class="['btn btn-blue h-100', props.size ? `btn-${props.size}` : ``]"
      :data-test-id="`tinyurl_${props.prefix}_share_dropdown_btn`"
    >
      <i class="fa-solid fa-fw fa-share-nodes" />
      <span class="d-none d-sm-inline ml-1">Share</span>
    </button>
  </Popper>
</template>
